/* ===================================================================
   Listings health (NAP) -- surfaces the scored Name/Address/Phone scan
   that Hopper shipped at GET /api/v1/listings/nap.

   LIVE DATA (no mocks): everything here comes from useListingsHealth().
   The screen is built to NEVER fabricate a number. The score, band,
   per-field values, suggested fixes, and the per-source directory rollup
   are exactly what the backend returned from real directory fetches.

   DIRECTORY-DRIVEN: rendering is driven by data.directory_items (one row
   per source, fixed order [google, foursquare, yelp, facebook, bing,
   apple]). New sources appear automatically -- nothing here is hardcoded
   to a fixed list of platforms. Each row carries a source_class that says
   exactly how to render it:

     real_scored + found    -> we read a real NAP record. Show the pulled
                               name/address/phone in the consistency table
                               with a green Match, plus a fix link if a
                               suggested_fix targets this source+field.
     real_scored + unknown  -> we could NOT confirm this source (e.g.
                               Foursquare before its API key lands). Show
                               "Not confirmed" honestly + the claim_url as a
                               "Set up / claim" link. NEVER a fabricated value.
     presence_only          -> Bing / Apple. nap is ALWAYS null. We NEVER
                               show name/address/phone. We surface only the
                               found/not-confirmed state + a "Claim / verify
                               your listing" call to action (claim_url).

   Sections:
     1. Score hero       -- 0-100 + band + plain-English summary.
     2. Breakdown        -- the three scoring components.
     3. Consistency table-- name/address/phone x each real_scored source.
     4. Fix these        -- suggested_fixes with their edit_url deep links.
     5. Get found        -- presence_only sources as claim cards.
   =================================================================== */
import { useEffect, useState } from 'react';
import {
  Card,
  CardHead,
  Chip,
  Progress,
  Icon,
  CheckPath,
  BrandGlyph,
  SearchPath,
  ClosePath,
} from '../components/ui';
import { useQueryClient } from '@tanstack/react-query';
import {
  useListingsHealth,
  useSessionToken,
  usePlacesSearch,
  useFindMyBusiness,
  useRemoveBusiness,
  useMe,
  qk,
} from '../hooks';
import type {
  ListingsHealth,
  ListingsBand,
  NapField,
  DirectoryItem,
  PlaceMatch,
} from '../lib/api';
import '../styles/components.css';
import '../styles/listings.css';

/* Debounce a fast-changing value (the business-name search box). Places search
   bills per call, so we only let the query settle after the owner stops typing.
   Same ~350ms pattern as the Competitors picker. */
function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return debounced;
}

/* Owner-friendly copy for the rare search failures. We never surface raw
   error codes (invalid_query / places_unavailable / lookup_failed). Mirrors
   the Competitors picker. */
function searchErrorMessage(err: Error): string {
  const code = (err as { detail?: string }).detail || err.message;
  if (code === 'places_unavailable') {
    return 'Business search is not available right now. Please try again in a little while.';
  }
  return 'Could not search just now. Please try again in a moment.';
}

const FIELDS: { id: NapField; label: string; sub: string }[] = [
  { id: 'name', label: 'Name', sub: 'Business name' },
  { id: 'address', label: 'Address', sub: 'Street address' },
  { id: 'phone', label: 'Phone', sub: 'Public number' },
];

/* Map a band to its CSS class + a token color for the hero ring. We keep the
   color token-driven (no hardcoded brand hex). */
function bandClass(band: ListingsBand): string {
  switch (band) {
    case 'Excellent':
      return 'excellent';
    case 'Good':
      return 'good';
    case 'Needs work':
      return 'needs-work';
    default:
      return 'at-risk';
  }
}
function bandColorVar(band: ListingsBand): string {
  switch (band) {
    case 'Excellent':
      return 'var(--green)';
    case 'Good':
      return 'var(--accent-soft)';
    case 'Needs work':
      return 'var(--gold)';
    default:
      return 'var(--red)';
  }
}

const FieldIco = (
  <>
    <path d="M12 2l8 3v6c0 5-3.5 9-8 10-4.5-1-8-5-8-10V5z" strokeLinejoin="round" />
    <path d="M9 12l2 2 4-4" strokeLinecap="round" strokeLinejoin="round" />
  </>
);
const ExtLink = (
  <>
    <path d="M14 4h6v6M20 4l-9 9" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M18 14v4a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2h4" strokeLinecap="round" strokeLinejoin="round" />
  </>
);
const PinIco = (
  <>
    <path d="M12 21s-7-5.5-7-11a7 7 0 0114 0c0 5.5-7 11-7 11z" strokeLinejoin="round" />
    <circle cx="12" cy="10" r="2.5" />
  </>
);

/* ---- "Why this matters" explainer ----
   The longer, plain-English "why" (Steve, 2026-06-29) so a non-technical
   owner fully gets the stakes. Shown at the top of BOTH the populated and the
   empty/first-run state. Token-driven styling (.nap-why) -- no hardcoded hex. */
function NapWhy() {
  return (
    <div className="nap-why">
      <span className="nap-why__ico" aria-hidden="true">
        <Icon d={FieldIco} />
      </span>
      <div>
        <b className="nap-why__head">Why this matters</b>
        <p>
          NAP is your <b>Name, Address, and Phone</b>. When all three match everywhere
          customers find you online, Google trusts your business and ranks you higher. When
          they don't, that trust slips and you lose calls you never even hear about. Rhody
          Reviews reads the directories it can check directly, scores you on what it actually
          finds there, and shows you exactly what to fix.
        </p>
      </div>
    </div>
  );
}

/* ---- "find your business" connect (NAP empty state + Change business) ----
   No Google sign-in / OAuth. The score only needs to IDENTIFY the business and
   read its PUBLIC listing, so we use the same friction-free name search as the
   Competitors picker: type your business name (+ optional city) -> pick it from
   the list -> we stamp that business onto your account and load your NAP score.
   The owner only ever sees name + address; the place_id rides along behind the
   scenes and is NEVER shown.

   REUSED for "Change business" on the populated state: when `mode === 'change'`
   we drop the big empty-state header, show a compact "Pick a different business"
   heading + a Cancel control, and call `onDone()` after a successful re-stamp so
   the caller can collapse the picker. The pick handler is otherwise identical --
   same useFindMyBusiness hook, same find-place endpoint, same invalidations --
   so swapping the connected business reuses exactly what already works. */
function NapFindBusiness({
  mode = 'empty',
  onDone,
  onCancel,
}: {
  mode?: 'empty' | 'change';
  onDone?: () => void;
  onCancel?: () => void;
}) {
  const qc = useQueryClient();
  const find = useFindMyBusiness();

  const [name, setName] = useState('');
  const [city, setCity] = useState('');
  const debouncedName = useDebounced(name, 350);
  const debouncedCity = useDebounced(city, 350);
  const search = usePlacesSearch(debouncedName, debouncedCity);

  const [picked, setPicked] = useState<PlaceMatch | null>(null);

  const typed = name.trim().length > 0;
  const settling = typed && debouncedName.trim() !== name.trim();
  const results = search.data?.results ?? [];
  const showNoMatches =
    typed && !settling && !search.isFetching && !search.isError && results.length === 0;

  async function handlePick(match: PlaceMatch) {
    setPicked(match);
    try {
      // Stamp the chosen business onto this tenant's own row. find-place
      // re-resolves name + full address server-side to the same business and
      // saves its place_id (clients.google_location_id) -- the exact column the
      // NAP scan reads. Passing the full street address pins the match.
      await find.findBusiness(match.name, match.address || undefined);
      // me is invalidated inside the hook; also refetch the NAP score so the
      // populated state reloads for the NEW business now that place_id changed.
      await qc.invalidateQueries({ queryKey: qk.listingsHealth() });
      // Let the caller collapse the picker (change mode); empty mode just
      // re-renders into the populated state once the score query returns.
      onDone?.();
    } catch {
      // Surfaced below; let the owner pick again.
      setPicked(null);
    }
  }

  const saving = find.isPending;
  const isChange = mode === 'change';

  return (
    <Card>
      {isChange ? (
        <div className="vhead" style={{ margin: '2px 0 14px' }}>
          <h2 style={{ fontSize: 18 }}>Pick a different business</h2>
          {onCancel && (
            <span
              className="link muted"
              onClick={() => { if (!saving) onCancel(); }}
              style={{ cursor: saving ? 'default' : 'pointer' }}
            >
              Cancel
            </span>
          )}
        </div>
      ) : (
        <div className="emptybox" style={{ paddingBottom: 18 }}>
          <BrandGlyph size={56} />
          <h3>Find your business to see your NAP score</h3>
          <p>
            {/* Was "across Google, Yelp, and Facebook" -- a straight false claim
                on the FIRST screen an owner sees. Yelp and Facebook are never
                checked; there is no way to read a business listing from either.
                (Brad, 2026-08-06.) */}
            Search your business name and pick it from the list. Rhody Reviews pulls your
            real public listing and scores how your name, address, and phone line up
            across the directories it can read. No sign-in needed.
          </p>
        </div>
      )}

      <div className="formgrid" style={{ maxWidth: 520, margin: '0 auto' }}>
        <label className="fld full">
          <span className="lab">Business name</span>
          <div className="srch-wrap">
            <span className="srch-ico"><Icon d={SearchPath} /></span>
            <input
              className="inp srch-inp"
              value={name}
              autoFocus
              placeholder="Start typing your business name"
              onChange={(e) => { setName(e.target.value); }}
              disabled={saving}
            />
          </div>
        </label>
        <label className="fld full">
          <span className="lab">City (optional)</span>
          <input
            className="inp"
            value={city}
            placeholder="e.g. North Kingstown, RI"
            onChange={(e) => setCity(e.target.value)}
            disabled={saving}
          />
        </label>
      </div>

      <div style={{ maxWidth: 520, margin: '0 auto' }}>
        {typed && (
          <div className="srch-results" role="listbox" aria-label="Matching businesses">
            {(search.isFetching || settling) && results.length === 0 && (
              <p className="muted srch-note">Searching...</p>
            )}

            {search.isError && (
              <p className="srch-note" style={{ color: 'var(--red)' }}>
                {searchErrorMessage(search.error)}
              </p>
            )}

            {results.map((m) => (
              <button
                key={m.place_id}
                className="srch-row"
                role="option"
                aria-selected="false"
                disabled={saving}
                onClick={() => void handlePick(m)}
              >
                <span className="srch-row__name">{m.name}</span>
                {m.address && <span className="srch-row__addr">{m.address}</span>}
              </button>
            ))}

            {showNoMatches && (
              <p className="muted srch-note">
                No matches. Try adding a city to narrow it down.
              </p>
            )}
          </div>
        )}

        {saving && picked && (
          <p className="muted srch-note">Connecting {picked.name} and loading your score...</p>
        )}

        {find.error && !saving && (
          <p style={{ color: 'var(--red)', fontSize: 13, marginTop: 10, textAlign: 'center' }}>
            Could not connect that business just now. Please pick it again.
          </p>
        )}

        {!typed && (
          <p className="muted" style={{ fontSize: 12, marginTop: 12, lineHeight: 1.5, textAlign: 'center' }}>
            Rhody Reviews uses this only to read your public listing. Nothing is posted or changed.
          </p>
        )}
      </div>
    </Card>
  );
}

/* ---- Score hero ----
   Conic-gradient donut, faithful to the Claude Design prototype: the outer
   ring fills to the real score fraction in the band color; the inner white
   disc carries the 0-100 number. The fill fraction is the actual score
   (clamped 0-100) -- never a fabricated value. */
function ScoreHero({ data }: { data: ListingsHealth }) {
  const color = bandColorVar(data.band);
  const pct = Math.max(0, Math.min(100, data.score));
  return (
    <Card>
      <div className="lh-hero">
        <div
          className="lh-score"
          style={{
            background: `conic-gradient(${color} 0 ${pct}%, var(--lh-track) ${pct}%)`,
          }}
        >
          <div className="lh-score__disc">
            <div className="v">{data.score}</div>
            <div className="o">/ 100</div>
          </div>
        </div>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <h2 className="lh-hero__title">NAP health</h2>
            <span className={'lh-band ' + bandClass(data.band)}>{data.band}</span>
          </div>
          <p className="lh-summary">{data.summary}</p>
        </div>
      </div>
    </Card>
  );
}

/* ---- Connected-business controls (populated state only) ----
   Two clear actions once a business IS connected (Steve, 2026-06-29):

     1. "Change business" -> reopens the same find-your-business search picker
        (NapFindBusiness in mode="change"). Picking a new one re-stamps via the
        existing find-place endpoint + invalidates listingsHealth, so the whole
        page reloads for the new business. Pure reuse of what already works.

     2. "Remove this business" -> disconnects the current business so the page
        returns to the find-business empty state. CONFIRM-gated so it is never a
        one-tap accident.

   REMOVE WIRED LIVE (Brad, 2026-06-29 PM): the backend now accepts
   google_location_id in the PUT /api/v1/business allow-list, and an empty
   string CLEARS it. So Remove is a real one-line wire-up: useRemoveBusiness()
   sends PUT {google_location_id: ''}, then invalidates me + listingsHealth so
   the page falls back to the find-business empty state. CONFIRM-gated so it is
   never a one-tap accident. (Supersedes the earlier "needs a backend update"
   honest-hold state.) */
function NapBusinessControls({
  businessLabel,
}: {
  businessLabel: string;
}) {
  const [changing, setChanging] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const removeBiz = useRemoveBusiness();

  // The clear-endpoint now exists (PUT /business {google_location_id: ''}), so
  // the confirm's primary action is live.
  const removeReady = true;

  async function doRemove() {
    try {
      await removeBiz.remove();
      // me + listingsHealth invalidated inside the hook -> the page re-renders
      // into the find-business empty state once the score query returns empty.
      setConfirming(false);
    } catch {
      // Surfaced in the modal below; leave the confirm open so they can retry.
    }
  }

  if (changing) {
    return (
      <div style={{ marginTop: 18 }}>
        <NapFindBusiness
          mode="change"
          onDone={() => setChanging(false)}
          onCancel={() => setChanging(false)}
        />
      </div>
    );
  }

  return (
    <>
      <div className="nap-actions">
        <div className="nap-actions__lab">
          <span className="muted">Connected business</span>
          <b>{businessLabel}</b>
        </div>
        <div className="nap-actions__btns">
          <button
            className="mini mini--ghost"
            type="button"
            onClick={() => setChanging(true)}
          >
            Change business
          </button>
          <button
            className="mini mini--ghost nap-actions__danger"
            type="button"
            onClick={() => setConfirming(true)}
          >
            Remove this business
          </button>
        </div>
      </div>

      {confirming && (
        <div
          className="modal-bg"
          onClick={(e) => { if (e.target === e.currentTarget) setConfirming(false); }}
        >
          <div className="modal" style={{ maxWidth: 440 }}>
            <div className="modal__head">
              <h3>Remove this business?</h3>
              <button className="x" onClick={() => setConfirming(false)} aria-label="Close">
                <Icon d={ClosePath} />
              </button>
            </div>
            <div className="modal__body">
              <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6 }}>
                Remove <b>{businessLabel}</b> from your NAP check? Your score and listings
                here will clear and you can connect a different business.
              </p>
              {removeBiz.error && (
                <div
                  className="alert info"
                  style={{ marginTop: 14, fontSize: 12.5, lineHeight: 1.55, color: 'var(--red)' }}
                >
                  Could not remove that business just now. Please try again.
                </div>
              )}
            </div>
            <div className="modal__foot">
              <button
                className="btn btn--ghost"
                onClick={() => setConfirming(false)}
                disabled={removeBiz.isPending}
              >
                Cancel
              </button>
              <button
                className="btn btn--primary nap-remove-btn"
                disabled={!removeReady || removeBiz.isPending}
                onClick={() => void doRemove()}
              >
                {removeBiz.isPending ? 'Removing...' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* ---- Score breakdown (the three components) ---- */
function Breakdown({ data }: { data: ListingsHealth }) {
  const b = data.score_breakdown;
  const rows = [
    {
      key: 'consistency',
      title: 'Field consistency',
      earned: b.field_consistency.earned,
      possible: b.field_consistency.possible,
      desc: 'Whether your name, address, and phone match across every listing that reported them.',
    },
    {
      key: 'presence',
      title: 'Presence & coverage',
      earned: b.presence.earned,
      possible: b.presence.possible,
      desc: 'How many of the directories Rhody Reviews can read you are actually found on.',
    },
    {
      key: 'no_gaps',
      title: 'No gaps',
      earned: b.no_gaps.earned,
      possible: b.no_gaps.possible,
      desc:
        b.no_gaps.deductions.length > 0
          ? 'Reduced where Rhody Reviews expected a listing but could not confirm it.'
          : 'No unconfirmed or duplicate listings detected.',
    },
  ];
  return (
    <Card>
      <CardHead title="Score breakdown" />
      <div className="lh-breakdown">
        {rows.map((r) => {
          const pct = r.possible > 0 ? Math.round((r.earned / r.possible) * 100) : 0;
          // round earned for display only; never alter the real value
          const earnedTxt = Number.isInteger(r.earned) ? String(r.earned) : r.earned.toFixed(0);
          return (
            <div className="lh-comp" key={r.key}>
              <div className="ct">
                <b>{r.title}</b>
                <span className="pts">
                  {earnedTxt} / {r.possible} pts
                </span>
              </div>
              <Progress pct={pct} />
              <div className="cd">{r.desc}</div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

/* ---- Per-source cell state for a given field (real_scored sources only) ----
   Honest by construction: the value comes straight from item.nap, which the
   backend only populates for real_scored + found. 'unknown'/'not_listed'
   sources have nap === null, so we render "Not confirmed" -- never a fake
   value. 'mismatch' only when a suggested_fix targets this source+field. */
function cellFor(
  data: ListingsHealth,
  item: DirectoryItem,
  field: NapField,
): { value: string | null; state: 'ok' | 'mismatch' | 'unknown'; stateLabel: string } {
  // No confirmed NAP record from this source -> honest "not confirmed".
  if (item.state !== 'found' || item.nap === null) {
    return {
      value: null,
      state: 'unknown',
      stateLabel: item.state === 'not_listed' ? 'Not listed' : 'Not confirmed',
    };
  }

  const value = (item.nap[field] ?? '').trim();
  if (!value) {
    // Found, but this particular field was blank on this source.
    return { value: null, state: 'unknown', stateLabel: 'No data' };
  }

  // Mismatch only when this field is inconsistent AND a fix targets this
  // source+field (the backend already decided the canonical value).
  const fieldRow = data.fields[field];
  const isInconsistent = fieldRow?.status === 'inconsistent';
  const flagged =
    !!isInconsistent &&
    data.suggested_fixes.some((f) => f.field === field && f.platform === item.platform);
  return {
    value,
    state: flagged ? 'mismatch' : 'ok',
    stateLabel: flagged ? 'Mismatch' : 'Match',
  };
}

/* ---- "Why Foursquare?" inline explainer ----
   Foursquare was wired in as a real_scored NAP source (2026-06-29). Owners who
   have never opened the Foursquare app will ask why it is here, so we attach a
   small expandable note directly to the Foursquare column in the consistency
   table. Token-driven styling (.nap-fsq), phone-friendly, ASCII-safe. Rendered
   only on the Foursquare header so it stays in context, never a page banner. */
function WhyFoursquare() {
  return (
    <details className="nap-fsq">
      <summary className="nap-fsq__sum">Why Foursquare?</summary>
      <p className="nap-fsq__body">
        Almost nobody opens the Foursquare app anymore, but its business data quietly
        powers Apple Maps, Bing, Siri, and other voice assistants. Keeping your info
        accurate here helps customers find you in all of those places.
      </p>
    </details>
  );
}

/* ---- Consistency table (real_scored sources) ---- */
function ConsistencyTable({
  data,
  sources,
}: {
  data: ListingsHealth;
  sources: DirectoryItem[];
}) {
  return (
    <Card style={{ padding: '8px 6px' }}>
      <div className="lh-tbl-scroll">
        <table className="tbl lh-tbl">
          <thead>
            <tr>
              <th>Field</th>
              {sources.map((s) => (
                <th key={s.platform}>
                  {s.label}
                  {s.platform === 'foursquare' && <WhyFoursquare />}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {FIELDS.map((f) => (
              <tr key={f.id}>
                <td>
                  <span className="fld-name">{f.label}</span>
                  <span className="fld-sub">{f.sub}</span>
                </td>
                {sources.map((s) => {
                  const c = cellFor(data, s, f.id);
                  return (
                    <td key={s.platform}>
                      <span className="cell">
                        <span className="cv">{c.value ?? '—'}</span>
                        <span className={'cstate ' + c.state}>{c.stateLabel}</span>
                      </span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Per-source "set up / claim" links for any real_scored source we could
          not confirm (e.g. Foursquare before its key lands). Honest -- no NAP
          shown, just a way to wire it up. */}
      {sources.some((s) => s.state !== 'found' && s.claim_url) && (
        <div className="lh-claimrow">
          {sources
            .filter((s) => s.state !== 'found' && s.claim_url)
            .map((s) => (
              <a
                key={s.platform}
                className="lh-claimlink"
                href={s.claim_url}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Icon d={ExtLink} sw={2} /> Set up {s.label}
              </a>
            ))}
        </div>
      )}
    </Card>
  );
}

/* ---- "Fix these" list ---- */
function FixList({ data }: { data: ListingsHealth }) {
  const fixes = data.suggested_fixes;
  return (
    <Card>
      <CardHead
        title="Fix these"
        right={
          fixes.length > 0 ? (
            <Chip kind="gold">{String(fixes.length)}</Chip>
          ) : (
            <Chip kind="green">All clear</Chip>
          )
        }
      />
      {fixes.length === 0 ? (
        <div className="lh-allclear">
          <span className="lh-allclear__ico" aria-hidden="true">
            <Icon d={CheckPath} sw={2.5} />
          </span>
          <p>
            Nothing to fix right now. Your details match across every listing Rhody Reviews
            could confirm.
          </p>
        </div>
      ) : (
        <div>
          {fixes.map((fx, i) => (
            <div className="lh-fix" key={`${fx.platform}-${fx.field}-${i}`}>
              <span className="alert info ico" style={{ display: 'grid' }}>
                <Icon d={FieldIco} />
              </span>
              <div className="t">
                <b>
                  {fx.platform.charAt(0).toUpperCase() + fx.platform.slice(1)} {fx.field}
                </b>
                <div className="d">
                  Shows <code>{fx.current || '—'}</code>. Your other listings show{' '}
                  <code>{fx.expected || '—'}</code>. Updating this keeps customers and
                  search engines from getting conflicting info.
                </div>
              </div>
              {fx.edit_url ? (
                <a
                  className="mini mini--go go"
                  href={fx.edit_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Icon d={ExtLink} sw={2} /> Fix on {fx.platform.charAt(0).toUpperCase() + fx.platform.slice(1)}
                </a>
              ) : (
                <span className="chip gray go">No edit link</span>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

/* ---- "Get found" -- presence-only sources (Bing / Apple) ----
   These sources have NO readable NAP API, so nap is ALWAYS null and we NEVER
   show name/address/phone. They are about being findable, not a NAP
   comparison. We show the found/not-confirmed state + a claim CTA. */
function PresenceCards({ sources }: { sources: DirectoryItem[] }) {
  if (sources.length === 0) return null;
  return (
    <Card>
      <CardHead title="Where to claim the rest" />
      {/* Say plainly that these were NOT checked, and why, in owner language.
          No API names, no technical reasons (feedback_no_dev_code_in_app_ui).
          Critically: do NOT invent a verdict for these and do NOT imply they
          were scanned. A false "not listed" is worse than an honest "unknown",
          which is exactly why the engine leaves them unknown. */}
      <p className="muted" style={{ fontSize: 12.5, lineHeight: 1.6, margin: '0 0 14px' }}>
        These directories don't let outside tools look up a business listing, so Rhody Reviews
        can't check them and won't guess. They aren't part of your score. Claim or verify each
        one yourself so your details are right where customers look.
      </p>
      <div className="lh-presence">
        {sources.map((s) => {
          const found = s.state === 'found';
          return (
            <div className="lh-pcard" key={s.platform}>
              <span className="lh-picon" aria-hidden="true">
                <Icon d={PinIco} sw={1.8} />
              </span>
              <div className="lh-pbody">
                <b>{s.label}</b>
                {/* "Not checked", not "Not confirmed". "Not confirmed" reads as
                    a verdict from a scan that ran and came back empty. Nothing
                    ran here, and saying so is the honest label. */}
                <span className={'lh-pstate ' + (found ? 'ok' : 'unknown')}>
                  {found ? 'Listing found' : 'Not checked'}
                </span>
              </div>
              {s.claim_url ? (
                <a
                  className="mini mini--go go"
                  href={s.claim_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Icon d={ExtLink} sw={2} /> Claim / verify
                </a>
              ) : (
                <span className="chip gray go">No link</span>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}

/* ---- Loading skeleton ---- */
function Skeleton() {
  return (
    <>
      <Card>
        <div className="lh-hero">
          <div
            className="lh-score"
            style={{ border: '4px solid var(--line-2)', background: 'var(--bg-2)' }}
          >
            <div className="muted" style={{ fontFamily: 'var(--font-display)', fontSize: 28 }}>
              &middot;&middot;
            </div>
          </div>
          <div style={{ flex: 1 }}>
            <div className="muted" style={{ padding: '6px 0' }}>Scanning your listings...</div>
            <div className="progress" style={{ maxWidth: 320 }}>
              <i style={{ width: '40%' }} />
            </div>
          </div>
        </div>
      </Card>
      <div className="vhead">
        <h2>Consistency</h2>
      </div>
      <Card>
        <div className="muted" style={{ padding: '20px 4px', textAlign: 'center' }}>
          Checking your directory listings...
        </div>
      </Card>
    </>
  );
}

export function Listings() {
  // Bridge the Supabase login to a data-plane rr_ token (deduped globally).
  useSessionToken();

  const { data, isLoading, isError, error, refetch, isFetching } = useListingsHealth();
  // Connected-business label for the Change/Remove controls + the Remove
  // confirm copy. Prefer the real connected listing's name; fall back to the
  // account's business name. Never shows the place_id.
  const me = useMe();

  if (isLoading) return <Skeleton />;

  if (isError) {
    return (
      <Card>
        <div style={{ padding: '28px 12px', textAlign: 'center' }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Could not scan your listings</div>
          <div className="muted" style={{ fontSize: 13, marginBottom: 16 }}>
            {error?.message ? error.message : 'Something went wrong reaching the listing sites.'}
          </div>
          <button className="btn btn--ghost" onClick={() => void refetch()}>
            Try again
          </button>
        </div>
      </Card>
    );
  }

  // First-run / empty: no platforms confirmed AND none even configured/unknown.
  // We refuse to show a fabricated "clean" score with nothing actually checked.
  const nothingChecked =
    !!data &&
    data.platforms_checked.length === 0 &&
    data.platforms_unknown.length === 0;

  if (!data || nothingChecked) {
    return (
      <div className="nap-screen">
        <NapWhy />
        <NapFindBusiness />
      </div>
    );
  }

  // Split the directory rollup by how each source must be rendered. Driven
  // entirely by the data -- no hardcoded platform list, so new sources the
  // backend adds show up automatically with the right treatment.
  const items: DirectoryItem[] = data.directory_items ?? [];
  const realScored = items.filter((s) => s.source_class === 'real_scored');
  const presenceOnly = items.filter((s) => s.source_class === 'presence_only');

  const anyUnconfirmed =
    realScored.some((s) => s.state !== 'found') || data.platforms_unknown.length > 0;

  // Label the connected business from the real Google listing if we read one,
  // else the account's business name, else a safe generic. Never the place_id.
  const googleItem = realScored.find((s) => s.platform === 'google');
  const businessLabel =
    (googleItem?.nap?.name ?? '').trim() ||
    (me.data?.business_name ?? '').trim() ||
    'your business';

  return (
    <div className="nap-screen">
      <NapWhy />
      <ScoreHero data={data} />
      <NapBusinessControls businessLabel={businessLabel} />

      <div className="row split" style={{ marginTop: 18 }}>
        <Breakdown data={data} />
        <FixList data={data} />
      </div>

      {realScored.length > 0 && (
        <>
          {/* THE SPLIT (Steve, 2026-08-06): "we check these two, here's where to
              claim the rest." Six sources used to render with nothing on screen
              saying only two of them are actually read, so an owner reasonably
              assumed six checks ran. The engine was always honest (it marks the
              unreadable ones unknown and never asserts absence); the UI was what
              oversold it. These two headings are what carry the distinction. */}
          <div className="vhead">
            <h2>What Rhody Reviews checks</h2>
            <span className="link" onClick={() => void refetch()} style={{ cursor: 'pointer' }}>
              {isFetching ? 'Refreshing...' : 'Re-scan'}
            </span>
          </div>
          <p className="muted" style={{ fontSize: 12.5, lineHeight: 1.6, margin: '0 0 12px' }}>
            Rhody Reviews reads these listings directly and compares your name, address, and
            phone across them. Your score comes only from what it actually finds here.
          </p>
          <ConsistencyTable data={data} sources={realScored} />
        </>
      )}

      {presenceOnly.length > 0 && (
        <div style={{ marginTop: 18 }}>
          <PresenceCards sources={presenceOnly} />
        </div>
      )}

      {/* Scoped to the CHECKED sources. "Not confirmed" only ever appears in
          the table above, where a read genuinely ran and came back empty. */}
      {anyUnconfirmed && (
        <p className="muted lh-footnote" style={{ fontSize: 12.5, marginTop: 14, lineHeight: 1.6 }}>
          In the table above, "Not confirmed" means Rhody Reviews tried that listing during this
          scan and couldn't reach it, so it isn't guessing at the details. Connecting or claiming
          it lets Rhody Reviews include it in your score.
        </p>
      )}
    </div>
  );
}
